import React from 'react';
import { useIntl } from 'react-intl';
import { components, MenuProps } from 'react-select';

import { Option } from '../../../types/Option';
import { Box } from '../../Box/Box';
import { Footer } from '../../Footer/Footer';
import { Icon } from '../../Icon/Icon';
import { Layout } from '../../Layout/Layout';
import { MenuOption } from '../../Menu/subcomponents/MenuOption';
import { Text } from '../../Text/Text';
import { getOptionSelectAll, getSelectAllLabel } from '../utils';

export const Menu = (props: MenuProps<Option, boolean>) => {
  const intl = useIntl();
  const { children, selectProps } = props;
  const {
    asyncValue,
    enableSelectAll,
    onCreateOption,
    footerAction,
    menuHeader,
    isAllSelected: nonAsyncIsAllSelected,
    isIndeterminate,
    isEmbedded,
    isMulti,
    inputValue,
    isLoading,
    selectAllCount,
    options,
    onChange,
    onMenuClose,
  } = selectProps;

  const enableCreateOption = Boolean(onCreateOption);
  const isAllSelected = asyncValue?.isAllSelected ?? nonAsyncIsAllSelected;
  const SELECT_ALL_OPTION = getOptionSelectAll(intl);

  const handleSelectAll = () => {
    if (isAllSelected) {
      onChange?.([], {
        action: 'deselect-option',
        option: SELECT_ALL_OPTION,
      });
    } else {
      onChange?.(options as Option[], {
        action: 'select-option',
        option: SELECT_ALL_OPTION,
      });
    }
  };

  return (
    <components.Menu {...props}>
      <Box
        role="menu"
        styles={[
          componentStyles.base,
          isEmbedded ? null : componentStyles.default,
        ]}
      >
        {menuHeader && (
          <Box borderBottom="border" py={2}>
            {menuHeader}
          </Box>
        )}
        {isMulti && enableSelectAll && Boolean(options?.length) && (
          <MenuOption
            enableIndicator
            isIndeterminate={isIndeterminate}
            isMulti
            isSelected={isAllSelected}
            option={{
              label: getSelectAllLabel(intl, selectAllCount, inputValue),
              value: SELECT_ALL_OPTION.value,
            }}
            onClick={handleSelectAll}
          />
        )}
        {isLoading ? (
          <Box px={4} py={2}>
            <Text preset="description">
              {intl.formatMessage({
                id: 'SelectMenu.loading',
                defaultMessage: 'Loading…',
                description:
                  'Loading message shown in select menu while options are being fetched',
              })}
            </Text>
          </Box>
        ) : (
          children
        )}
        {(footerAction || enableCreateOption) && (
          <Footer
            isEmbedded={!enableCreateOption}
            actions={[
              {
                ...footerAction,
                onClick: () => {
                  footerAction.onClick();
                  onMenuClose?.(); // always close the menu
                },
              },
            ]}
            justify="flex-start"
            size="s"
          >
            {enableCreateOption ? (
              <Layout align="center" spacing={1}>
                <Icon color="status.info" icon="help" />
                <Text preset="help">
                  {intl.formatMessage({
                    id: 'SelectMenu.typeToSearchOrCreate',
                    defaultMessage: 'Type to search or create new option',
                    description:
                      'Help text shown in select menu to indicate users can type to search or create options',
                  })}
                </Text>
              </Layout>
            ) : null}
          </Footer>
        )}
      </Box>
    </components.Menu>
  );
};

export const componentStyles = {
  base: {
    marginTop: 1,
  },
  default: {
    backgroundColor: 'background',
    zIndex: 'forward',
  },
};
